<template>
  <div class="sms-report-wrapper">
    <va-card class="mb-4">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 class="m-0 font-weight-bold">SMS MESSAGE REPORT</h4>
          <p class="text-muted small m-0">Records saved after SMS requests are submitted to Telinfy.</p>
        </div>
        <b-badge variant="success" class="p-2">LOCAL SEND LOG</b-badge>
      </div>

      <!-- Summary Metrics Grid -->
      <b-row class="mb-4">
        <b-col md="3" sm="6" class="mb-3">
          <div class="summary-box border-left-primary shadow-sm p-3 rounded bg-light">
            <div class="text-muted text-uppercase small font-weight-bold">Total SMS</div>
            <div class="h3 font-weight-bold mb-0 text-dark">{{ formatNumber(summary.total) }}</div>
            <small class="text-secondary">{{ sentRateText }}</small>
          </div>
        </b-col>
        <b-col md="3" sm="6" class="mb-3">
          <div class="summary-box border-left-success shadow-sm p-3 rounded bg-light">
            <div class="text-muted text-uppercase small font-weight-bold">Sent</div>
            <div class="h3 font-weight-bold mb-0 text-success">{{ formatNumber(summary.sent) }}</div>
            <small class="text-secondary">Accepted by Telinfy</small>
          </div>
        </b-col>
        <b-col md="3" sm="6" class="mb-3">
          <div class="summary-box border-left-danger shadow-sm p-3 rounded bg-light">
            <div class="text-muted text-uppercase small font-weight-bold">Failed</div>
            <div class="h3 font-weight-bold mb-0 text-danger">{{ formatNumber(summary.failed) }}</div>
            <small class="text-secondary">Request not accepted</small>
          </div>
        </b-col>
        <b-col md="3" sm="6" class="mb-3">
          <div class="summary-box border-left-warning shadow-sm p-3 rounded bg-light">
            <div class="text-muted text-uppercase small font-weight-bold">Units Used</div>
            <div class="h3 font-weight-bold mb-0 text-warning">{{ formatNumber(summary.units) }}</div>
            <small class="text-secondary">SMS billing units</small>
          </div>
        </b-col>
      </b-row>

      <!-- Filter Panel -->
      <b-card class="bg-light mb-4 border-0">
        <!-- <div class="d-flex justify-content-between align-items-center mb-3">
          <h6 class="font-weight-bold m-0"><i class="fa fa-filter mr-1"></i> FIND MESSAGE RECORDS</h6>
          <b-button variant="link" class="p-0 text-muted" :disabled="loading || exporting" @click="resetFilters">
            <i class="fa fa-refresh mr-1"></i> Reset
          </b-button>
        </div> -->
        <b-form @submit.prevent="applyFilters">
          <b-row>
            <b-col md="4" sm="12" class="mb-3">
              <label class="small text-muted font-weight-bold text-uppercase">Search</label>
              <b-form-input v-model.trim="filters.keyword" placeholder="Recipient, type, or message" />
            </b-col>
            <b-col md="2" sm="6" class="mb-3">
              <label class="small text-muted font-weight-bold text-uppercase">Source</label>
              <b-form-select v-model="filters.source" :options="sourceOptions" />
            </b-col>
            <b-col md="2" sm="6" class="mb-3">
              <label class="small text-muted font-weight-bold text-uppercase">Status</label>
              <b-form-select v-model="filters.status" :options="statusOptions" />
            </b-col>
            <b-col md="2" sm="6" class="mb-3">
              <label class="small text-muted font-weight-bold text-uppercase">From</label>
              <va-date-picker
                v-model="filters.startDate"
                :config="{ dateFormat: 'd-m-Y' }"
                placeholder="DD-MM-YYYY"
              />
            </b-col>
            <b-col md="2" sm="6" class="mb-3">
              <label class="small text-muted font-weight-bold text-uppercase">To</label>
              <va-date-picker
                v-model="filters.endDate"
                :config="{ dateFormat: 'd-m-Y' }"
                placeholder="DD-MM-YYYY"
              />
            </b-col>
          </b-row>
          <div class="d-flex justify-content-end">
            <b-button type="submit" variant="info" class="mr-2" :disabled="loading || exporting">
              <i v-if="!loading" class="fa fa-search mr-1"></i>
              <b-spinner v-else small class="mr-1"></b-spinner>
              Apply
            </b-button>
            <b-button variant="outline-success" class="mr-2" :disabled="loading || exporting || total === 0" @click="exportReport('excel')">
              <i class="fa fa-file-excel-o mr-1"></i> Excel
            </b-button>
            <b-button variant="outline-danger" :disabled="loading || exporting || total === 0" @click="exportReport('pdf')">
              <i class="fa fa-file-pdf-o mr-1"></i> PDF
            </b-button>
          </div>
        </b-form>
      </b-card>

      <!-- Data Table -->
      <div class="d-flex justify-content-between align-items-center mb-3">
        <span class="text-muted small" v-if="!loading">{{ rangeText }}</span>
        <div class="d-flex align-items-center">
          <span class="small text-muted mr-2">Rows per page:</span>
          <b-form-select v-model.number="limit" :options="[10, 25, 50]" size="sm" style="width: 70px;" @change="changeLimit" />
        </div>
      </div>

      <div v-if="loading" class="text-center py-5">
        <b-spinner variant="info" label="Loading..."></b-spinner>
        <p class="text-muted mt-2">Loading SMS records...</p>
      </div>

      <div v-else-if="normalizedRows.length === 0" class="text-center py-5 text-muted">
        <i class="fa fa-envelope-o fa-3x mb-3"></i>
        <h5>No SMS records found</h5>
        <p class="small">Try adjusting your filters or date range.</p>
      </div>

      <div v-else class="table-responsive">
        <table class="table table-hover table-striped border">
          <thead class="thead-dark">
            <tr>
              <th>Date & Time</th>
              <th>Source</th>
              <th>Type</th>
              <th>Recipient</th>
              <th>Message</th>
              <th>Gateway Status</th>
              <th>Gateway Message</th>
              <th class="text-right">Units</th>
              <th class="text-center">Status</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in normalizedRows" :key="row.id">
              <td class="small white-space-nowrap">
                <strong>{{ row.date }}</strong><br />
                <span class="text-muted">{{ row.time }}</span>
              </td>
              <td><span class="badge badge-secondary">{{ row.source }}</span></td>
              <td class="font-weight-bold">{{ row.campaign }}</td>
              <td class="text-nowrap">{{ row.recipient }}</td>
              <td>
                <div class="message-text text-truncate" style="max-width: 280px;" :title="row.message">{{ row.message }}</div>
                <small v-if="row.error" class="text-danger d-block text-truncate" style="max-width: 280px;" :title="row.error">{{ row.error }}</small>
              </td>
              <td class="small">{{ row.gatewayStatus }}</td>
              <td class="small text-muted" style="max-width: 200px;">
                <div class="text-truncate" :title="row.gatewayMessage">{{ row.gatewayMessage }}</div>
              </td>
              <td class="text-right font-weight-bold">{{ formatNumber(row.units) }}</td>
              <td class="text-center">
                <b-badge :variant="row.status === 'sent' ? 'success' : 'danger'">
                  {{ row.statusLabel }}
                </b-badge>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination Footer -->
      <div v-if="!loading && total > 0" class="d-flex justify-content-between align-items-center mt-3">
        <span class="small text-muted">{{ rangeText }}</span>
        <b-pagination
          v-model="page"
          :total-rows="total"
          :per-page="limit"
          size="sm"
          class="m-0"
          @change="goToPage"
        />
      </div>
    </va-card>
  </div>
</template>

<script>
import axios from 'axios'


function localDateValue (date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${day}-${month}-${year}`
}

function currentMonthRange () {
  const today = new Date()
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1)
  const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0)

  return {
    startDate: localDateValue(monthStart),
    endDate: localDateValue(monthEnd)
  }
}

export default {
  name: 'SmsReport',
  data () {
    return {
      loading: false,
      exporting: false,
      rawRows: [],
      page: 1,
      limit: 25,
      total: 0,
      totalPages: 1,
      summary: { total: 0, sent: 0, failed: 0, units: 0 },
      filters: Object.assign({ keyword: '', source: '', status: '' }, currentMonthRange()),
      sourceOptions: [
        { value: '', text: 'All sources' },
        { value: 'marketing', text: 'Marketing' },
        { value: 'notification', text: 'Notification' }
      ],
      statusOptions: [
        { value: '', text: 'All statuses' },
        { value: 'sent', text: 'Sent' },
        { value: 'failed', text: 'Failed' }
      ]
    }
  },
  computed: {
    normalizedRows () {
      return this.rawRows.map((row, index) => this.normalizeRow(row, index))
    },
    sentRateText () {
      return this.summary.total > 0
        ? `${Math.round((this.summary.sent / this.summary.total) * 100)}% sent rate`
        : 'No activity yet'
    },
    rangeText () {
      if (!this.total) return '0 records'
      const start = ((this.page - 1) * this.limit) + 1
      const end = Math.min(this.page * this.limit, this.total)
      return `${this.formatNumber(start)}–${this.formatNumber(end)} of ${this.formatNumber(this.total)} records`
    }
  },
  mounted () {
    this.fetchReport()
  },
  methods: {
    getToken () {
      return localStorage.getItem('token') || localStorage.getItem('appToken') || ''
    },
    queryParams (page, limit) {
      return {
        page,
        limit,
        keyword: this.filters.keyword,
        source: this.filters.source,
        channel: 'sms',
        status: this.filters.status,
        startDate: this.filters.startDate,
        endDate: this.filters.endDate
      }
    },
    fetchReport () {
      this.loading = true
      const token = this.getToken()
      return axios.get('/v1/admin/messages/report', {
        headers: { token, authorization: token },
        params: this.queryParams(this.page, this.limit)
      }).then(response => {
        const body = response && response.data ? response.data : {}
        if (body.status === false) {
          throw new Error(body.message || 'Error loading SMS report')
        }
        const data = body.data || {}
        this.rawRows = Array.isArray(data.rows) ? data.rows : []
        this.total = Number.isFinite(Number(data.total)) ? Number(data.total) : this.rawRows.length
        this.page = Math.max(1, Number(data.page) || this.page)
        this.limit = Math.max(1, Number(data.limit) || this.limit)
        this.totalPages = Math.max(1, Number(data.totalPage) || 1)
        this.summary = this.normalizeSummary(data.summary)
      }).catch(err => {
        console.error('Failed to fetch SMS report:', err)
        this.rawRows = []
        this.total = 0
      }).finally(() => {
        this.loading = false
      })
    },
    applyFilters () {
      this.page = 1
      this.fetchReport()
    },
    resetFilters () {
      this.filters = Object.assign({ keyword: '', source: '', status: '' }, currentMonthRange())
      this.page = 1
      this.fetchReport()
    },
    changeLimit () {
      this.page = 1
      this.fetchReport()
    },
    goToPage (newPage) {
      this.page = newPage
      this.fetchReport()
    },
    normalizeSummary (summary) {
      const root = summary && typeof summary === 'object' ? summary : {}
      const totals = root.totals || {}
      const sms = (root.byChannel && root.byChannel.sms) || {}
      const merged = Object.assign({}, totals, sms)
      return {
        total: Number(merged.total) || 0,
        sent: Number(merged.sent) || 0,
        failed: Number(merged.failed) || 0,
        units: Number(merged.units) || 0
      }
    },
    normalizeRow (row, index) {
      const source = this.titleCase(row && row.source ? row.source : 'Unknown')
      const status = row && row.status ? String(row.status).toLowerCase() : 'unknown'
      const rawDate = row && (row.sentAt || row.createdAt)
      const parsedDate = rawDate ? new Date(rawDate) : null
      const validDate = parsedDate && !Number.isNaN(parsedDate.getTime())
      return {
        id: (row && row._id) || `${rawDate || 'row'}-${index}`,
        dateTime: validDate ? `${localDateValue(parsedDate)} ${parsedDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : '—',
        date: validDate ? localDateValue(parsedDate) : '—',
        time: validDate ? parsedDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '',
        source,
        campaign: this.campaignLabel(row),
        recipient: (row && row.recipient) || '—',
        message: (row && row.message) || '—',
        length: Number(row && row.length) || 0,
        segments: Number(row && row.segments) || 1,
        gatewayStatus: this.gatewayStatusLabel(row),
        gatewayMessage: this.gatewayMessageValue(row && row.gatewayResponse),
        units: Number(row && row.units) || 1,
        status,
        statusLabel: this.titleCase(status),
        error: (row && row.error) || ''
      }
    },
    campaignLabel (row) {
      if (!row) return '—'
      return this.titleCase(row.notificationType || 'OTP')
    },
    gatewayStatusLabel (row) {
      if (!row) return '—'
      const status = row.gatewayStatus || '—'
      return row.gatewayStatusCode ? `${status} (HTTP ${row.gatewayStatusCode})` : status
    },
    gatewayMessageValue (response, depth = 0) {
      if (response === undefined || response === null || response === '') return '—'

      if (typeof response === 'string') {
        const text = response.trim()
        if (!text) return '—'
        const looksLikeJSON = (text[0] === '{' && text[text.length - 1] === '}') || (text[0] === '[' && text[text.length - 1] === ']')
        if (depth < 2 && looksLikeJSON) {
          try {
            return this.gatewayMessageValue(JSON.parse(text), depth + 1)
          } catch (error) {
            return text
          }
        }
        return text
      }

      if (Array.isArray(response)) return response.length ? this.gatewayMessageValue(response[0], depth + 1) : '—'
      if (typeof response !== 'object') return String(response)

      const providerResponse = Array.isArray(response.responses) ? response.responses[0] : null
      if (providerResponse && providerResponse.message) return this.gatewayMessageValue(providerResponse.message, depth + 1)
      if (response.message) return this.gatewayMessageValue(response.message, depth + 1)
      if (response.error) return this.gatewayMessageValue(response.error, depth + 1)
      return '—'
    },
    titleCase (value) {
      return String(value).replace(/[_-]+/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    },
    formatNumber (val) {
      return new Intl.NumberFormat().format(Number(val) || 0)
    },
    exportReport (format) {
      this.exporting = true
      const token = this.getToken()
      return axios.get('/v1/admin/messages/report', {
        headers: { token, authorization: token },
        params: this.queryParams(1, 5000)
      }).then(response => {
        const body = response && response.data ? response.data : {}
        const records = (body.data && Array.isArray(body.data.rows)) ? body.data.rows : []
        if (!records.length) {
          alert('No SMS records available to export.')
          return
        }
        const exportData = records.map((row, index) => this.normalizeRow(row, index))
        if (format === 'excel') {
          this.downloadCSV(exportData)
        } else {
          this.generatePDF(exportData)
        }
      }).catch(err => {
        console.error('Export error:', err)
        alert('Failed to export SMS report.')
      }).finally(() => {
        this.exporting = false
      })
    },
    downloadCSV (records) {
      const headers = ['Date & Time', 'Source', 'Type', 'Recipient', 'Message', 'Gateway Status', 'Gateway Message', 'Units', 'Status']
      const csvRows = [headers.join(',')]

      records.forEach(r => {
        const row = [
          `"${r.dateTime}"`,
          `"${r.source}"`,
          `"${r.campaign}"`,
          `"${r.recipient}"`,
          `"${r.message.replace(/"/g, '""')}"`,
          `"${r.gatewayStatus}"`,
          `"${r.gatewayMessage.replace(/"/g, '""')}"`,
          r.units,
          `"${r.statusLabel}"`
        ]
        csvRows.push(row.join(','))
      })

      const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `CyberHawk_SMS_Report_${new Date().toISOString().slice(0, 10)}.csv`
      a.click()
      window.URL.revokeObjectURL(url)
    },
    generatePDF (records) {
      const jsPDFModule = require('jspdf')
      const jsPDF = jsPDFModule.jsPDF || jsPDFModule
      const doc = new jsPDF('landscape', 'mm', 'a4')
      doc.setFillColor(23, 63, 53)
      doc.rect(0, 0, 297, 20, 'F')
      doc.setTextColor(255, 255, 255)
      doc.setFontSize(14)
      doc.text('CyberHawk SMS Message Report', 10, 13)

      let y = 30
      doc.setTextColor(0, 0, 0)
      doc.setFontSize(8)
      doc.text('Generated on: ' + new Date().toLocaleString(), 10, 25)

      records.forEach((r, idx) => {
        if (y > 185) {
          doc.addPage()
          y = 20
        }
        const text = `${idx + 1}. [${r.dateTime}] ${r.recipient} | ${r.campaign} | Status: ${r.statusLabel} | Units: ${r.units} | Message: ${r.message.slice(0, 60)}`
        doc.text(text, 10, y)
        y += 7
      })

      doc.save(`CyberHawk_SMS_Report_${new Date().toISOString().slice(0, 10)}.pdf`)
    }
  }
}
</script>

<style scoped>
.sms-report-wrapper {
  padding: 15px;
}
.summary-box {
  border-left: 4px solid;
}
.border-left-primary { border-left-color: #007bff !important; }
.border-left-success { border-left-color: #28a745 !important; }
.border-left-danger { border-left-color: #dc3545 !important; }
.border-left-warning { border-left-color: #ffc107 !important; }
.white-space-nowrap { white-space: nowrap; }

::v-deep .va-date-picker {
  width: 100%;
}
</style>
